/**
 * What pausing or closing means for the listing's posts on other sites (listing-marketing-hub
 * BR-15b), inside the Pause and Close confirmations: each posted record by name and date, or — when
 * none is recorded — how to add one first so Resida can remind the landlord to take it down.
 * Never says the posts will be paused or removed: Resida cannot touch them.
 */
import { AlertTriangle } from 'lucide-react';
import { asIsoDateTimeString, formatTimestamp } from '@resida/shared';
import { LISTING_SITE_LABELS, type ExternalListingPostDto } from '../../lib/external-listing-posts.api';
import { postedPosts } from '../../lib/listing-marketing';

interface PostsImpactListProps {
  action: 'pause' | 'close';
  posts: ExternalListingPostDto[] | undefined;
  /** Property timezone for the post dates; dates are omitted until it is known. */
  timezone: string | undefined;
}

const ADD_UNRECORDED = 'Posted somewhere you have not recorded? Add it on the Marketing tab first so Resida can remind you.';

export function PostsImpactList({ action, posts, timezone }: PostsImpactListProps) {
  const posted = postedPosts(posts);
  const testId = `${action}-posts-impact`;

  if (posted.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={testId} data-state="none">
        {ADD_UNRECORDED}
      </p>
    );
  }

  const day = (iso: string) =>
    timezone ? formatTimestamp(asIsoDateTimeString(iso), timezone, { month: 'short', day: 'numeric' }) : null;

  return (
    <div
      className="flex items-start gap-3 rounded-md border border-warning/25 bg-warning/10 p-3 text-sm"
      data-testid={testId}
      data-state="posted"
    >
      <AlertTriangle aria-hidden="true" className="mt-0.5 h-[18px] w-[18px] shrink-0 text-warning" />
      <div className="min-w-0 space-y-2">
        <p className="font-semibold text-warning">Your posts stay live.</p>
        <p className="text-foreground">
          Resida cannot {action === 'pause' ? 'pause' : 'take down'} posts on other sites. After{' '}
          {action === 'pause' ? 'pausing' : 'closing'}, delete these on each site and mark them removed, or renters
          will keep finding them:
        </p>
        <ul className="list-disc space-y-0.5 pl-5 text-foreground" data-testid={`${testId}-list`}>
          {posted.map((p) => {
            const date = day(p.postedAt);
            return (
              <li key={p.id}>
                {LISTING_SITE_LABELS[p.site]}
                {date ? `, posted ${date}` : ''}
              </li>
            );
          })}
        </ul>
        <p className="text-muted-foreground">{ADD_UNRECORDED}</p>
      </div>
    </div>
  );
}
